"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { AuthContext } from "@/components/auth/Auth";
import { TabToggle } from "@/components/common/TabToggle";
import SandboxedExternalIframe from "@/components/common/SandboxedExternalIframe";
import type { CommonSelectItem } from "@/components/utils/select/CommonSelect";
import CommonTabs from "@/components/utils/select/tabs/CommonTabs";
import FilePreview from "./file-upload/components/FilePreview";
import UploadArea from "./file-upload/components/UploadArea";
import BrowserWarning from "./file-upload/components/BrowserWarning";
import useFileUploader from "./file-upload/hooks/useFileUploader";
import useDragAndDrop from "./file-upload/hooks/useDragAndDrop";
import useAccessibility from "./file-upload/hooks/useAccessibility";
import type { MemesArtSubmissionFileProps } from "./file-upload/reducers/types";
import { isBrowserSupported } from "./file-upload/utils/browserDetection";
import { SUBMISSION_FILE_INPUT_ACCEPT } from "@/constants/submission-media.constants";
import { ALLOWED_INTERACTIVE_MEDIA_MIME_TYPES, DEFAULT_INTERACTIVE_MEDIA_MIME_TYPE, INTERACTIVE_MEDIA_PROVIDERS, type InteractiveMediaProvider } from "./submission/constants/media";
const renderPreviewMessage = (primary: string, secondary: string) => <div className="tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-p-6 tw-text-center">
    <span className="tw-mb-2 tw-text-sm tw-font-medium tw-tracking-wide tw-text-iron-300">
      {primary}
    </span>
    <span className="tw-text-xs tw-font-medium tw-tracking-wide tw-text-iron-500">
      {secondary}
    </span>
  </div>;

/**
 * Memes Art Submission File Component
 *
 * Handles uploading, previewing, and validating artwork files for meme submissions.
 * Supports images (PNG, JPG) and videos with browser compatibility detection.
 *
 * This version uses Blob URLs (createObjectURL) to preview any newly selected file,
 * which satisfies a CSP requiring 'blob:' rather than 'data:' for media.
 */
const MemesArtSubmissionFile: React.FC<MemesArtSubmissionFileProps> = t0 => {
  const $ = _c(90);
  const {
    artworkUploaded,
    artworkUrl,
    uploadError,
    artworkMimeType,
    setArtworkUploaded,
    handleFileSelect,
    mediaSource,
    setMediaSource,
    externalHash,
    externalProvider,
    externalConstructedUrl,
    externalPreviewUrl,
    externalMimeType,
    externalError,
    externalValidationStatus,
    isExternalMediaValid,
    onExternalHashChange,
    onExternalProviderChange,
    onExternalMimeTypeChange,
    onClearExternalMedia
  } = t0;
  const {
    setToast
  } = useContext(AuthContext);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      supported: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [browserSupport, setBrowserSupport] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = typeof window !== "undefined" ? window.matchMedia("(prefers-reduced-motion: reduce)").matches : false;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const prefersReducedMotion = t2;
  let t3;
  if ($[2] !== handleFileSelect || $[3] !== setArtworkUploaded || $[4] !== setToast) {
    t3 = {
      onFileSelect: handleFileSelect,
      setUploaded: setArtworkUploaded,
      showToast: setToast
    };
    $[2] = handleFileSelect;
    $[3] = setArtworkUploaded;
    $[4] = setToast;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    state,
    processFile,
    handleRetry,
    handleRemoveFile,
    resetState,
    dispatch
  } = useFileUploader(t3);
  let t4;
  if ($[6] !== dispatch) {
    t4 = newState => {
      dispatch({
        type: "SET_VISUAL_STATE",
        payload: newState
      });
    };
    $[6] = dispatch;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const setVisualState = t4;
  const fileInputRef = useRef(null);
  let t5;
  if ($[8] !== mediaSource) {
    t5 = () => {
      if (mediaSource !== "upload") {
        return;
      }
      fileInputRef.current?.click();
    };
    $[8] = mediaSource;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleAreaClick = t5;
  const t6 = mediaSource === "upload" && !artworkUploaded;
  let t7;
  if ($[10] !== processFile || $[11] !== setVisualState || $[12] !== t6) {
    t7 = {
      enabled: t6,
      onFileDrop: processFile,
      setVisualState
    };
    $[10] = processFile;
    $[11] = setVisualState;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const {
    dropAreaRef,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop
  } = useDragAndDrop(t7);
  const t8 = mediaSource === "upload" && !artworkUploaded;
  let t9;
  if ($[14] !== handleAreaClick || $[15] !== t8) {
    t9 = {
      isActive: t8,
      onAreaClick: handleAreaClick,
      prefersReducedMotion
    };
    $[14] = handleAreaClick;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const {
    handleKeyDown
  } = useAccessibility(t9);
  let t10;
  if ($[17] !== mediaSource || $[18] !== processFile) {
    t10 = e => {
      const file = e.target.files?.[0];
      if (file) {
        if (mediaSource === "upload") {
          processFile(file);
        }
        e.target.value = "";
      }
    };
    $[17] = mediaSource;
    $[18] = processFile;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleFileInputChange = t10;
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [{
      key: "upload",
      label: "Upload File",
      panelId: "memes-art-submission-upload-panel"
    }, {
      key: "url",
      label: "Interactive HTML",
      panelId: "memes-art-submission-interactive-panel"
    }];
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const tabOptions = t11 as const;
  let t12;
  if ($[21] !== onExternalMimeTypeChange || $[22] !== setMediaSource) {
    t12 = key => {
      if (key === "upload") {
        setMediaSource("upload");
        return;
      }
      if (key === "url") {
        onExternalMimeTypeChange(DEFAULT_INTERACTIVE_MEDIA_MIME_TYPE);
        setMediaSource("url");
      }
    };
    $[21] = onExternalMimeTypeChange;
    $[22] = setMediaSource;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const handleTabSelect = t12;
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = INTERACTIVE_MEDIA_PROVIDERS.map(_temp);
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const providerOptions = t13;
  let t14;
  if ($[25] !== externalProvider || $[26] !== onExternalProviderChange) {
    t14 = provider_0 => {
      if (provider_0 === externalProvider) {
        return;
      }
      onExternalProviderChange(provider_0);
    };
    $[25] = externalProvider;
    $[26] = onExternalProviderChange;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const handleProviderSelect = t14;
  let t15;
  let t16;
  if ($[28] !== setToast) {
    t15 = () => {
      const support = isBrowserSupported();
      setBrowserSupport(support);
      if (!support.supported && support.reason) {
        setToast({
          type: "warning",
          message: support.reason
        });
      }
    };
    t16 = [setToast];
    $[28] = setToast;
    $[29] = t15;
    $[30] = t16;
  } else {
    t15 = $[29];
    t16 = $[30];
  }
  useEffect(t15, t16);
  const {
    visualState,
    error,
    currentFile,
    objectUrl,
    videoCompatibility,
    isCheckingCompatibility
  } = state;
  const displayUploadError = uploadError ?? error;
  const uploadVisualState = uploadError ? "invalid" : visualState;
  let t17;
  let t18;
  if ($[31] !== resetState || $[32] !== uploadError) {
    t17 = () => {
      if (uploadError) {
        resetState();
      }
    };
    t18 = [resetState, uploadError];
    $[31] = resetState;
    $[32] = uploadError;
    $[33] = t17;
    $[34] = t18;
  } else {
    t17 = $[33];
    t18 = $[34];
  }
  useEffect(t17, t18);
  const [previewUrl, setPreviewUrl] = useState(artworkUrl);
  let t19;
  let t20;
  if ($[35] !== artworkUrl || $[36] !== currentFile || $[37] !== objectUrl) {
    t19 = () => {
      if (currentFile) {
        const blobUrl = URL.createObjectURL(currentFile);
        setPreviewUrl(blobUrl);
        return () => {
          URL.revokeObjectURL(blobUrl);
        };
      }
      setPreviewUrl(objectUrl ?? artworkUrl);
    };
    t20 = [currentFile, objectUrl, artworkUrl];
    $[35] = artworkUrl;
    $[36] = currentFile;
    $[37] = objectUrl;
    $[38] = t19;
    $[39] = t20;
  } else {
    t19 = $[38];
    t20 = $[39];
  }
  useEffect(t19, t20);
  const showUploadUi = mediaSource === "upload";
  let t21;
  bb0: {
    if (externalValidationStatus === "pending") {
      let t22;
      if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
        t22 = renderPreviewMessage("Validating preview...", "We're verifying the gateway serves an HTML document.");
        $[40] = t22;
      } else {
        t22 = $[40];
      }
      t21 = t22;
      break bb0;
    }
    if (externalValidationStatus === "invalid" && externalError) {
      let t22;
      if ($[41] !== externalError) {
        t22 = renderPreviewMessage(externalError, "Only approved IPFS or Arweave gateway HTML documents can be embedded.");
        $[41] = externalError;
        $[42] = t22;
      } else {
        t22 = $[42];
      }
      t21 = t22;
      break bb0;
    }
    let t22;
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = renderPreviewMessage("Provide a valid hash or CID to enable the preview.", "The final artwork is rendered securely inside a sandboxed iframe.");
      $[43] = t22;
    } else {
      t22 = $[43];
    }
    t21 = t22;
  }
  const previewFallback = t21;
  let t22;
  if ($[44] !== externalMimeType) {
    t22 = ALLOWED_INTERACTIVE_MEDIA_MIME_TYPES.find(type => type.value === externalMimeType);
    $[44] = externalMimeType;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  const match = t22;
  const mediaTypeLabel = match?.label ?? externalMimeType;
  let t23;
  if ($[46] !== handleTabSelect || $[47] !== mediaSource) {
    t23 = <div className="tw-mb-4 tw-flex-shrink-0 tw-border-0 tw-border-b tw-border-solid tw-border-iron-800/60"><TabToggle options={tabOptions} activeKey={mediaSource} onSelect={handleTabSelect} /></div>;
    $[46] = handleTabSelect;
    $[47] = mediaSource;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  const t24 = `tw-min-h-0 tw-flex-1 tw-overflow-hidden ${showUploadUi ? "" : "tw-pb-4 desktop-hover:hover:tw-scrollbar-thumb-iron-300 lg:tw-overflow-y-auto lg:tw-scrollbar-thin lg:tw-scrollbar-track-iron-800 lg:tw-scrollbar-thumb-iron-500"}`;
  let t25;
  if ($[49] !== artworkMimeType || $[50] !== artworkUploaded || $[51] !== browserSupport || $[52] !== currentFile || $[53] !== displayUploadError || $[54] !== dropAreaRef || $[55] !== externalConstructedUrl || $[56] !== externalError || $[57] !== externalHash || $[58] !== externalPreviewUrl || $[59] !== externalProvider || $[60] !== handleAreaClick || $[61] !== handleDragEnter || $[62] !== handleDragLeave || $[63] !== handleDragOver || $[64] !== handleDrop || $[65] !== handleFileInputChange || $[66] !== handleKeyDown || $[67] !== handleProviderSelect || $[68] !== handleRemoveFile || $[69] !== handleRetry || $[70] !== isCheckingCompatibility || $[71] !== isExternalMediaValid || $[72] !== mediaTypeLabel || $[73] !== onClearExternalMedia || $[74] !== onExternalHashChange || $[75] !== previewFallback || $[76] !== previewUrl || $[77] !== showUploadUi || $[78] !== state.hasRecoveryOption || $[79] !== uploadError || $[80] !== uploadVisualState || $[81] !== videoCompatibility || $[82] !== visualState) {
    t25 = showUploadUi ? <motion.div id="memes-art-submission-upload-panel" ref={dropAreaRef} {...!artworkUploaded && !prefersReducedMotion ? {
      whileHover: {
        scale: 1.002
      }
    } : {}} className={`tw-group tw-relative tw-min-h-[280px] tw-w-full tw-overflow-hidden tw-rounded-xl tw-bg-gradient-to-br tw-from-iron-900 tw-to-iron-950 sm:tw-min-h-[340px] lg:tw-h-full lg:tw-min-h-0 ${visualState === "dragging" ? "tw-border-2 tw-border-primary-500/60" : ""} ${uploadVisualState === "invalid" ? "tw-border-2 tw-border-red/60" : ""} ${visualState === "idle" && !artworkUploaded ? "hover:tw-border hover:tw-border-iron-700/80" : ""} tw-transition-all tw-duration-300 ${artworkUploaded ? "" : "tw-cursor-pointer"} `} onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={!artworkUploaded ? handleAreaClick : undefined} role={artworkUploaded ? undefined : "button"} tabIndex={artworkUploaded ? -1 : 0} aria-label="Upload artwork" onKeyDown={handleKeyDown} aria-describedby={displayUploadError ? "file-upload-error" : undefined} data-testid="artwork-upload-area"><input ref={fileInputRef} type="file" accept={SUBMISSION_FILE_INPUT_ACCEPT} className="tw-hidden" onChange={handleFileInputChange} data-testid="artwork-file-input" />{!browserSupport.supported && browserSupport.reason && <BrowserWarning reason={browserSupport.reason} />}{!artworkUploaded ? <UploadArea visualState={uploadVisualState} error={displayUploadError} hasRecoveryOption={uploadError ? false : state.hasRecoveryOption} onRetry={handleRetry} /> : <FilePreview url={previewUrl} file={currentFile} mimeType={artworkMimeType} onRemove={handleRemoveFile} videoCompatibility={videoCompatibility} isCheckingCompatibility={isCheckingCompatibility} />}</motion.div> : <div id="memes-art-submission-interactive-panel" className="tw-flex tw-min-h-[280px] tw-flex-col tw-gap-6 tw-p-4 sm:tw-min-h-[340px] lg:tw-min-h-full"><div className="tw-flex tw-flex-col tw-gap-2"><span className="tw-text-sm tw-font-medium tw-text-iron-200">Hosting Network</span><div className="tw-w-fit tw-max-w-full"><CommonTabs items={providerOptions} activeItem={externalProvider} filterLabel="Select hosting network" setSelected={handleProviderSelect} size="sm" fill={false} /></div></div><div className="tw-flex tw-flex-col tw-gap-2"><label htmlFor="memes-interactive-media-hash" className="tw-text-sm tw-font-medium tw-text-iron-200">Content Hash or Path</label><input id="memes-interactive-media-hash" type="text" autoComplete="off" className="tw-form-input tw-w-full tw-cursor-text tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-px-3 tw-py-2.5 tw-text-base tw-font-normal tw-text-iron-100 tw-outline-none tw-ring-1 tw-ring-iron-700 tw-transition-all tw-duration-500 tw-ease-in-out placeholder:tw-text-iron-500 focus-visible:tw-ring-2 focus-visible:tw-ring-primary-400 focus-visible:hover:tw-ring-primary-400 desktop-hover:hover:tw-ring-iron-650 sm:tw-text-sm" placeholder="bafy.../index.html" value={externalHash} onChange={event => onExternalHashChange(event.target.value)} aria-invalid={Boolean(externalError)} />{externalError && <p className="tw-mb-0 tw-text-xs tw-font-medium tw-text-iron-400">{externalError}</p>}{externalConstructedUrl && !externalError && <p className="tw-text-xs tw-text-iron-400">Resulting URL{" "}<span className="tw-text-iron-100">{externalConstructedUrl}</span></p>}</div><div className="tw-flex tw-flex-col tw-gap-y-4"><div className="tw-flex tw-flex-col tw-gap-2"><span className="tw-text-sm tw-font-medium tw-text-iron-200">Media Type</span><div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-700 tw-py-2 tw-text-sm tw-text-iron-200"><span>{mediaTypeLabel}</span><span className="tw-text-xs tw-font-medium tw-text-iron-500">Fixed to interactive HTML (text/html)</span></div></div><div className="tw-flex tw-items-center"><button type="button" onClick={onClearExternalMedia} className="tw-rounded-md tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-iron-200 tw-transition disabled:tw-cursor-not-allowed disabled:tw-opacity-40 desktop-hover:hover:tw-bg-iron-800" disabled={!externalHash}>Clear Hash</button></div><div className="tw-flex tw-min-h-[260px] tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-950 sm:tw-min-h-[320px] lg:tw-min-h-[360px]">{isExternalMediaValid ? <SandboxedExternalIframe key={externalPreviewUrl} src={externalPreviewUrl} title="Interactive artwork preview" className="tw-bg-transparent" containerClassName="tw-flex-1 tw-flex tw-flex-col" fallback={renderPreviewMessage("Preview unavailable for unapproved domains or file types.", "Only approved IPFS or Arweave gateway HTML documents can be embedded.")} /> : <div className="tw-flex tw-h-full tw-flex-1 tw-items-center tw-justify-center">{previewFallback}</div>}</div></div></div>;
    $[49] = artworkMimeType;
    $[50] = artworkUploaded;
    $[51] = browserSupport;
    $[52] = currentFile;
    $[53] = displayUploadError;
    $[54] = dropAreaRef;
    $[55] = externalConstructedUrl;
    $[56] = externalError;
    $[57] = externalHash;
    $[58] = externalPreviewUrl;
    $[59] = externalProvider;
    $[60] = handleAreaClick;
    $[61] = handleDragEnter;
    $[62] = handleDragLeave;
    $[63] = handleDragOver;
    $[64] = handleDrop;
    $[65] = handleFileInputChange;
    $[66] = handleKeyDown;
    $[67] = handleProviderSelect;
    $[68] = handleRemoveFile;
    $[69] = handleRetry;
    $[70] = isCheckingCompatibility;
    $[71] = isExternalMediaValid;
    $[72] = mediaTypeLabel;
    $[73] = onClearExternalMedia;
    $[74] = onExternalHashChange;
    $[75] = previewFallback;
    $[76] = previewUrl;
    $[77] = showUploadUi;
    $[78] = state.hasRecoveryOption;
    $[79] = uploadError;
    $[80] = uploadVisualState;
    $[81] = videoCompatibility;
    $[82] = visualState;
    $[83] = t25;
  } else {
    t25 = $[83];
  }
  let t26;
  if ($[84] !== t24 || $[85] !== t25) {
    t26 = <div className={t24}>{t25}</div>;
    $[84] = t24;
    $[85] = t25;
    $[86] = t26;
  } else {
    t26 = $[86];
  }
  let t27;
  if ($[87] !== t23 || $[88] !== t26) {
    t27 = <div className="tw-flex tw-min-h-0 tw-flex-col lg:tw-h-full">{t23}{t26}</div>;
    $[87] = t23;
    $[88] = t26;
    $[89] = t27;
  } else {
    t27 = $[89];
  }
  return t27;
};
export default React.memo(MemesArtSubmissionFile);
function _temp(provider) {
  return {
    key: provider.key,
    label: provider.label,
    value: provider.key
  };
}